import * as THREE from 'three'
import { render } from 'react-dom'
import './styles.css'
import { useRef, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Center, OrbitControls, Instances, Instance } from '@react-three/drei'
import { SimpleReverb } from './SimpleReverb'
import allColors from './Colors'

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]

let cIndexes = new Array(allColors.length).fill().map((_, i) => i)
let ci = cIndexes.splice(randI(cIndexes.length), 1)[0]
let ci2 = cIndexes.splice(randI(cIndexes.length), 1)[0]
let ci3 = cIndexes.splice(randI(cIndexes.length), 1)[0]
let cols = allColors[ci]
let cols2 = allColors[ci2]
let cols3 = allColors[ci3]
cols.shift()
let bg = 'rgb(15, 14, 18)'
cols2.shift()
cols3.shift()
cols = [...cols, ...cols2, ...cols3]

let steps_per_frame = 3
let params = ({
  mu_k: rand([4,6,8]), sigma_k: 1.0, w_k: 0.022,
  mu_g: rand([0.4, 0.5, 0.6, 0.7, 0.2]), sigma_g: 0.2, c_rep: 0.06 + ((randI(14) * 0.01)),
  dt: 0.2
})

let point_n = 666
let offset = [
  (3 + randI(12)) * 0.02 * rand([1, -1]),
  (3 + randI(12)) * 0.02 * rand([1, -1])
]
let colz = [[rand(cols), rand(cols)]]
let pointCols = [colz]
let allNotes = [80, 83, 85, 87, 90, 92, 95, 97, 99, 102, 104, 107, 109, 111, 114, 116, 119, 121]

let notes = []
let rotations = new Array(point_n).fill(0)
let currNote = rand(allNotes)

function initPoints(points) {
  let rad = 1 + randI(8)
  let chance = rand([492,494,496,497,498])
  for (let i = 0; i < point_n; ++i) {
    if (randI(500) > chance) {
      offset = [
        (3 + randI(12)) * 0.02 * rand([1, -1]),
        (3 + randI(12)) * 0.02 * rand([1, -1])
      ]

      rad = rad * rand([0.5, 1, 1.5, 0.25]) * rand([1, -1])
      currNote = rand(allNotes)
      colz = [rand(cols), rand(cols)]
    }

    let fr = (i + 1) / point_n
    points[(i * 2)] = (offset[0] + (Math.cos((fr * Math.PI * rad)) * ((i) * 0.1 * offset[0])));
    points[(i * 2 + 1)] = (offset[1] + (Math.sin((fr * Math.PI * rad)) * ((i) * 0.15 * offset[1])));
    pointCols.push(colz)
    notes.push(currNote)
  }
  return points;
}
let points = initPoints(new Float32Array(point_n * 2));
let fields = {
  R_val: new Float32Array(point_n),
  U_val: new Float32Array(point_n),
  R_grad: new Float32Array(point_n * 2),
  U_grad: new Float32Array(point_n * 2),
}
function add_xy(a, i, x, y, c) {
  a[i * 2] += x * c; a[i * 2 + 1] += y * c;
}
function compute_fields() {
  const { R_val, U_val, R_grad, U_grad } = fields;
  const { c_rep, mu_k, sigma_k, w_k } = params;
  R_val.fill(repulsion_f(0.0, c_rep)[0]);
  U_val.fill(peak_f(0, mu_k, sigma_k, w_k)[0]);
  R_grad.fill(0); U_grad.fill(0);

  for (let i = 0; i < point_n - 1; ++i)
    for (let j = i + 1; j < point_n; ++j) {
      let rx = points[i * 2] - points[j * 2];
      let ry = points[i * 2 + 1] - points[j * 2 + 1];
      const r = Math.sqrt(rx * rx + ry * ry) + 1e-20;
      rx /= r; ry /= r;

      if (r < 1.0) {
        const [R, dR] = repulsion_f(r, c_rep);
        add_xy(R_grad, i, rx, ry, dR);
        add_xy(R_grad, j, rx, ry, -dR);
        R_val[i] += R; R_val[j] += R;
      }
      const [K, dK] = peak_f(r, mu_k, sigma_k, w_k);
      add_xy(U_grad, i, rx, ry, dK);
      add_xy(U_grad, j, rx, ry, -dK);
      U_val[i] += K; U_val[j] += K;
    }
}

function repulsion_f(x, c_rep) {
  const t = Math.max(1.0 - x, 0.0);
  return [0.5 * c_rep * t * t, -c_rep * t];
}

function fast_exp(x) {
  let t = 1.0 + x / 32.0;
  t *= t; t *= t; t *= t; t *= t; t *= t; // t **= 32
  return t;
}

function peak_f(x, mu, sigma, w = 1.0) {
  const t = (x - mu) / sigma;
  const y = w / fast_exp(t * t);
  return [y, -1 * t * y / sigma];
}

function step() {
  const { R_val, U_val, R_grad, U_grad } = fields;
  const { mu_g, sigma_g, dt } = params;
  compute_fields();
  let total_E = 0.0;
  for (let i = 0; i < point_n; ++i) {
    const [G, dG] = peak_f(U_val[i], mu_g, sigma_g);
    const vx = dG * U_grad[i * 2] - R_grad[i * 2];
    const vy = dG * U_grad[i * 2 + 1] - R_grad[i * 2 + 1];
    add_xy(points, i, vx, vy, dt);
    total_E += R_val[i] - G;
  }
  return total_E / point_n;
}
const isMobile = window.innerWidth < 600

export default function App() {
  const ref = useRef()
  return (
    <div id="main" onClick={() => {
      if (!audioStarted) {
        for (let c of click_osc) c.start();
        whiteNoise.start(0);
        audioStarted = true;
        fgains.map((_, i) => {
          fadeIn(i)
        })
      }
      randFilters()
    }}>
      <Canvas flat={false} linear={true} dpr={[1, 2]} camera={{
        near: 0.000001, far: 10000, position: [0, 0, isMobile ? 60 : 50], fov: 60,
      }}
        gl={{ alpha: false, antialias: true, stencil: false, depth: false }}
      >
        <color attach="background" args={[bg]} />
        <Grids />
        <ambientLight intensity={0.8} />
        <directionalLight intensity={1} position={[-1, 0, -1]} />
        <OrbitControls ref={ref} />
      </Canvas>
    </div>
  )
}
function Grids() {
  useFrame((e) => {
    for (let i = 0; i < steps_per_frame; ++i) step();
  })
  return (
    <Center>
      <group rotation={[0, 0, randI(0) * Math.PI * 0.1]}>
        <Instances limit={100000} geometry={new THREE.TetrahedronGeometry(1, 0)} material={new THREE.MeshStandardMaterial({ wireframe: true, depthTest: false, depthWrite: false })}>
          {new Array(point_n).fill().map((_, i) =>
            <Thang i={i} key={i} />
          )}
        </Instances>
        <Instances limit={100000} geometry={new THREE.TetrahedronGeometry(5, 0)} material={new THREE.MeshBasicMaterial({ visible: false })}>
          {new Array(point_n).fill().map((_, i) =>
            <MouseCatcher i={i} key={i} />
          )}
        </Instances>
      </group>
    </Center>
  )
}
let randFilters = (all) => {
  for (let f of filters) {
    randI(10) > (all ? 0 : 8) && (f.frequency.value = (2 + randI(5)) * 100)
  }
}
let click = (e) => {
  play()
  loops.push(e.instanceId)
  prevLoops.push(-1)
  loopCounters.push(0)
  loopMod.push(rand([1,2,3,4,5]) * 2)
}

let col1 = new THREE.Color()
let col2 = new THREE.Color()
let playingCol = rand(['red'])
function Thang({ i }) {
  const ref = useRef();
  const ref2 = useRef();
  useFrame(() => {
    ref.current.position.x = points[i * 2]
    ref.current.position.y = points[i * 2 + 1]
    ref.current.scale.setScalar(params.c_rep / (fields.R_val[i] * 5.0))
    col1.set(pointCols[i][0])
    col2.set(pointCols[i][1])
    ref2.current.color.set(loops.indexOf(i) !== -1 ? playingCol : col1.lerp(col2, Math.cos(((fields.R_val[i] * 40) % 1) * Math.PI * 2)))
    ref2.current.rotation.x = fields.R_grad[i * 2] * Math.PI * 4
    ref2.current.rotation.y = fields.R_grad[i * 2 + 1] * Math.PI * 4
    ref2.current.rotation.z = THREE.MathUtils.lerp(ref2.current.rotation.z, (fields.R_val[i] * Math.PI * 10) + rotations[i], 0.1)
  })
  return (
    <group ref={ref} onClick={click} position={[points[i * 2], points[i * 2 + 1], 0]} >
      <Instance ref={ref2} />
    </group>
  )
}
function MouseCatcher({ i }) {
  const ref = useRef();
  const ref2 = useRef();
  useFrame(() => {
    ref.current.position.x = points[i * 2]
    ref.current.position.y = points[i * 2 + 1]
    ref.current.scale.setScalar(params.c_rep / (fields.R_val[i] * 5.0))
    ref2.current.rotation.x = fields.R_grad[i * 2] * Math.PI * 4
    ref2.current.rotation.y = fields.R_grad[i * 2 + 1] * Math.PI * 4
    ref2.current.rotation.z = THREE.MathUtils.lerp(ref2.current.rotation.z, (fields.R_val[i] * Math.PI * 10) + rotations[i], 0.2)
  })
  return (
    <group ref={ref} position={[points[i * 2], points[i * 2 + 1], 0]} >
      <Instance ref={ref2} onPointerOver={() => {
        rotations[i] += rand([Math.PI, -Math.PI])
        playClickSound(randI(NUM), notes[i])

      }} />
    </group>
  )
}

render(
  <Suspense fallback={null}><App /></Suspense>,
  document.getElementById('root')
)

window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext = new AudioContext();
let audioStarted = false;
let bufferSize = 2 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
for (let i = 0; i < bufferSize; i++) {
  output[i] = Math.random() * 4 - 2;
}

let whiteNoise = audioContext.createBufferSource();
whiteNoise.buffer = noiseBuffer;
whiteNoise.loop = true;

let reverb = SimpleReverb(audioContext);
reverb.connect(audioContext.destination);
reverb.time = 2
reverb.wet.value = 0.5
reverb.dry.value = 0.1
reverb.filterType = 'lowpass'
reverb.cutoff.value = 100

let NUM = 7;
let filters = [];
let fgains = [];

for (let i = 0; i < NUM; i++) {
  let f = audioContext.createBiquadFilter();
  f.type = "bandpass";
  f.Q.value = 200;;
  filters.push(f);
  let g = audioContext.createGain();
  g.gain.value = 0;
  fgains.push(g);
}
randFilters(true)

for (let i = 0; i < NUM; i++) {
  whiteNoise.connect(filters[i]);
  filters[i].connect(fgains[i]);
  fgains[i].connect(reverb);
}
function fadeIn(id) {
  let ct = audioContext.currentTime;
  fgains[id].gain.linearRampToValueAtTime(0.2, ct + 8);
}
let click_osc = [];
let panNode = [];
let click_gain = [];
for (let i = 0; i < NUM; i++) {
  let c = audioContext.createOscillator();
  c.type = 'triangle';
  let p = audioContext.createStereoPanner();
  p.pan.value = -1;
  c.connect(p);
  let cg = audioContext.createGain();
  cg.gain.value = 0;
  p.connect(cg);
  cg.connect(reverb)
  click_osc.push(c);
  panNode.push(p);
  click_gain.push(cg);
}

function playClickSound(i, n) {
  click_osc[i].frequency.setValueAtTime(noteToFrequency(n), 0);
  let attack = parseFloat(0.001);
  let decay = parseFloat(0.0002);
  let sustain = parseFloat(0.006);
  let release = parseFloat(0.001);
  let ct = audioContext.currentTime;
  panNode[i].pan.value = -1 + (randI(20) * 0.1)
  click_gain[i].gain.linearRampToValueAtTime(0, ct);
  click_gain[i].gain.linearRampToValueAtTime(((1.5 + randI(30)) * 0.01), ct + attack);
  click_gain[i].gain.linearRampToValueAtTime((1.5 + randI(3)) * 0.01, ct + attack + decay);
  click_gain[i].gain.linearRampToValueAtTime((2 + randI(2)) * 0.01, ct + attack + decay + sustain);
  click_gain[i].gain.linearRampToValueAtTime(0, ct + attack + decay + sustain + release);
}

function noteToFrequency(note) {
  let a = 440; //frequency of A (common value is 440Hz)
  return (a / 32) * (2 ** ((note - 9) / 12));
}

let loops = []
let prevLoops = []
let loopCounters = []
let loopMod = []
let stuck = false
let beatCount = 0

function beat() {
  loops.forEach((l, li) => {
    if (!(beatCount % loopMod[li])) {
      for (var i = 0; i < point_n; i++) {
        let j = randI(point_n)
        let d = Math.abs(points[j * 2] - points[l * 2])
        let d2 = Math.abs(points[j * 2 + 1] - points[l * 2 + 1])
        if (
          d &&
          d2 &&
          d < 1.1 &&
          d2 < 1.1 &&
          j !== li &&
          j !== prevLoops[li]
        ) {
          prevLoops[li] = loops[li]
          loops[li] = j
          // fields.R_val[j] = 0.5
          loopCounters[li]--;
          stuck = false
          rotations[j] += (Math.PI * 0.5) * rand([1, -1])
          playClickSound(Math.abs(loopCounters[li]) % NUM, notes[j])
          break;
        } else {
          stuck = true
        }
      }
      loopCounters[li]++
    }
  })
  if (stuck) {
    loopCounters.forEach((lc, i) => {
      if (lc > 2) {
        loopCounters.splice(i, 1)
        prevLoops.splice(i, 1)
        loops.splice(i, 1)
        loopMod.splice(i, 1)
        randFilters()
      }
    })
  }
  beatCount++;
}

function scheduleNote(beatNumber, time) {
  notesInQueue.push({ note: beatNumber, time: time });
  beat()
}
function scheduler() {
  while (nextNoteTime < audioContext.currentTime + scheduleAheadTime) {
    scheduleNote(current16thNote, nextNoteTime);
    nextNote();
  }
}

function nextNote() {
  var secondsPerBeat = 60.0 / bpm;
  nextNoteTime += 0.25 * secondsPerBeat;
  current16thNote++;
  if (current16thNote == 16) {
    current16thNote = 0;
  }
}

var isPlaying = false;
var current16thNote;
var bpm = 600
var lookahead = 5.0;
var scheduleAheadTime = 0.1;
var nextNoteTime = 0.0;
var notesInQueue = [];
var timerWorker = null;
function play() {
  isPlaying = true;
  if (isPlaying) {
    current16thNote = 0;
    nextNoteTime = audioContext.currentTime;
    timerWorker.postMessage("start");
    return "stop";
  } else {
    timerWorker.postMessage("stop");
    return "play";
  }
}

function init() {
  timerWorker = new Worker("./metronomeworker.js");
  timerWorker.onmessage = function (e) {
    if (e.data == "tick") {
      scheduler();
    }
    else
      console.log("message: " + e.data);
  };
  timerWorker.postMessage({ "interval": lookahead });
}
window.addEventListener("load", init);
console.log('%c 🦠🦠 @ramioso 2023-01-18 🦠🦠', 'color: orange');
const feats = window.$fxhashFeatures = {
}